import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import { Skeleton } from "@tql/ui-components-react/tql/indicators";
import { alertTriangle } from "@tql/ui-components-react/untitled/icons";
import { useState } from "react";

import type { LtlItemType } from "../flyout-details";
import styles from "./drawer-item-details.module.css";

type DrawerItemDetailsProps = {
	readonly isLoading: boolean;
	readonly items: LtlItemType[];
};

export type LtlItem = {
	id: number;
	description: string | null;
	unitTypeId: number;
	unitTypeName: string | null;
	quantity: number;
	weight: number;
	weightUomId: number;
	length: number;
	width: number;
	height: number;
	dimensionUomId: number;
	freightClassCode: string | null;
	nmfcNumber: string | null;
	isStackable: boolean | null;
	isHazmat: boolean | null;
};

function getDisplayValue(value: string | number | null | undefined): string {
	if (value === null || value === undefined || value === "") {
		return "-";
	}

	return String(value);
}

function getYesNoValue(isLoaded: boolean | null | undefined): string {
	if (isLoaded === null || isLoaded === undefined) {
		return "-";
	}

	return isLoaded ? "Yes" : "No";
}

const CENTIMETERS = 1;
const INCHES = 2.54 * CENTIMETERS;
const FEET = 12 * INCHES;
const METERS = 100 * CENTIMETERS;

const LBS = 1;
const KILOGRAMS = 2.204_62 * LBS;

function getLengthUnits(id: number): number {
	return (
		{
			"1": INCHES,
			"2": CENTIMETERS,
			"3": FEET,
			"4": METERS,
		}[id] ?? INCHES // default to INCHES
	);
}

function getWeightUnits(id: number): number {
	return (
		{
			"1": LBS,
			"2": KILOGRAMS,
		}[id] ?? LBS // default to LBS
	);
}

function calculateDensity(
	item: LtlItemType | undefined,
	densityUnits: number,
): string {
	if (item == null) return "-";

	const dimensionsUnits = getLengthUnits(item.dimensionUomId);
	const weightUnits = getWeightUnits(item.weightUomId);

	if (
		item == null ||
		item.length <= 0 ||
		item.height <= 0 ||
		item.width <= 0 ||
		dimensionsUnits == undefined ||
		weightUnits == undefined
	)
		return "-";

	const weight = item.weight * weightUnits;
	const length = item.length * dimensionsUnits;
	const width = item.width * dimensionsUnits;
	const height = item.height * dimensionsUnits;
	const volume = length * width * height;
	const density = weight / volume;
	const densityScalar = density / densityUnits;
	return densityScalar.toFixed(2);
}

const displayLength = { abrev: "in", units: INCHES };
const displayWeight = { abrev: "lbs", units: LBS };
const displayDensity = { abrev: "lbs/ft³", units: LBS / (FEET * FEET * FEET) };

function getItemUnitScalars(item: LtlItemType) {
	const weightUnits = getWeightUnits(item.weightUomId);
	const dimUnits = getLengthUnits(item.dimensionUomId);
	const weightScalar = (item.weight * weightUnits) / displayWeight.units;
	const lengthScalar = (item.length * dimUnits) / displayLength.units;
	const widthScalar = (item.width * dimUnits) / displayLength.units;
	const heightScalar = (item.height * dimUnits) / displayLength.units;
	return { weightScalar, lengthScalar, widthScalar, heightScalar };
}

export function DrawerItemDetails({
	isLoading,
	items,
}: Readonly<DrawerItemDetailsProps>) {
	const [selectedRow, setSelectedRow] = useState<number>(0);

	const totalItems = items.reduce((sum, item) => sum + (item.quantity ?? 0), 0);
	const totalWeightScalar =
		items.reduce(
			(sum, item) => sum + item.weight * getWeightUnits(item.weightUomId),
			0,
		) / displayWeight.units;

	const selectedItem = items[selectedRow];

	const density = calculateDensity(selectedItem, displayDensity.units);

	const description = getDisplayValue(selectedItem?.description);
	const freightClass = getDisplayValue(selectedItem?.freightClassCode);
	const nmfcCode = getDisplayValue(selectedItem?.nmfcNumber);
	const hazmat = getYesNoValue(selectedItem?.isHazmat);
	const stackable = getYesNoValue(selectedItem?.isStackable);

	return (
		<div className={styles.wrapper}>
			<section aria-label={"Freight data"} className={styles.section}>
				<div className={styles.metaGrid}>
					<div className={styles.metaItem}>
						<p className={styles.label}>Total Units</p>
						{isLoading ? (
							<span className={styles.loadingState}>
								<Skeleton animation={{ type: "wave" }} />
							</span>
						) : (
							<p className={styles.value}>
								{totalItems.toLocaleString("en-US")}
							</p>
						)}
					</div>
					<div className={styles.metaItem}>
						<p className={styles.label}>Total Weight</p>
						{isLoading ? (
							<span className={styles.loadingState}>
								<Skeleton animation={{ type: "wave" }} />
							</span>
						) : (
							<p className={styles.value}>
								{Math.round(totalWeightScalar).toLocaleString("en-US")}{" "}
								{displayWeight.abrev}.
							</p>
						)}
					</div>
				</div>

				<div className={styles.tableWrap}>
					<table className={styles.table}>
						<thead>
							<tr>
								<th>Qty</th>
								<th>Unit</th>
								<th>{displayWeight.abrev.toUpperCase()}</th>
								<th>L ({displayLength.abrev})</th>
								<th>W ({displayLength.abrev})</th>
								<th>H ({displayLength.abrev})</th>
							</tr>
						</thead>
						<tbody>
							{items.map((item, index) => {
								const {
									weightScalar,
									lengthScalar,
									widthScalar,
									heightScalar,
								} = getItemUnitScalars(item);
								return (
									<tr
										key={item.id}
										className={`${index === selectedRow ? styles.selected : ""}`}
										onClick={() => setSelectedRow(index)}
									>
										<td>
											<div className={styles.cellContent}>
												{item.isHazmat ? (
													<SvgIcon
														className={styles.hazmatIcon}
														icon={alertTriangle}
													/>
												) : null}
												{item.quantity?.toLocaleString("en-US") ?? "-"}
											</div>
										</td>
										<td className={styles.units}>
											{item.unitTypeName ?? "[unknown]"}
										</td>
										<td>{Math.round(weightScalar).toLocaleString("en-US")}</td>
										<td>{Math.round(lengthScalar)}</td>
										<td>{Math.round(widthScalar)}</td>
										<td>{Math.round(heightScalar)}</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>

				<div className={styles.metaGrid}>
					<div className={styles.metaItem}>
						<p className={styles.label}>Description</p>
						{isLoading ? (
							<span className={styles.loadingState}>
								<Skeleton animation={{ type: "wave" }} />
							</span>
						) : (
							<p className={styles.value}>{description}</p>
						)}
					</div>
					<div className={styles.metaItem}>
						<p className={styles.label}>Class</p>
						{isLoading ? (
							<span className={styles.loadingState}>
								<Skeleton animation={{ type: "wave" }} />
							</span>
						) : (
							<p className={styles.value}>{freightClass}</p>
						)}
					</div>
					<div className={styles.metaItem}>
						<p className={styles.label}>NMFC Code</p>
						{isLoading ? (
							<span className={styles.loadingState}>
								<Skeleton animation={{ type: "wave" }} />
							</span>
						) : (
							<p className={styles.value}>{nmfcCode}</p>
						)}
					</div>
					<div className={styles.metaItem}>
						<p className={styles.label}>Density</p>
						{isLoading ? (
							<span className={styles.loadingState}>
								<Skeleton animation={{ type: "wave" }} />
							</span>
						) : (
							<p className={styles.value}>
								{density} {displayDensity.abrev}
							</p>
						)}
					</div>
					<div className={styles.metaItem}>
						<p className={styles.label}>Hazmat</p>
						{isLoading ? (
							<span className={styles.loadingState}>
								<Skeleton animation={{ type: "wave" }} />
							</span>
						) : (
							<p className={styles.value}>{hazmat}</p>
						)}
					</div>
					<div className={styles.metaItem}>
						<p className={styles.label}>Stackable</p>
						{isLoading ? (
							<span className={styles.loadingState}>
								<Skeleton animation={{ type: "wave" }} />
							</span>
						) : (
							<p className={styles.value}>{stackable}</p>
						)}
					</div>
				</div>
			</section>
		</div>
	);
}
